<template>
  <div class="pa-3">
    <div class="ml-2">
      <div class="pull-left">
        <v-icon
          v-if="!isMinimized"
          size="18"
          @click="handleClickMinimize"
          title="Hide results"
          >fas fa-arrow-left</v-icon
        >
        <v-icon
          v-if="isMinimized"
          size="18"
          @click="handleClickMinimize"
          title="Show results"
          >fas fa-arrow-right</v-icon
        >
      </div>
      <div class="pull-left">
        <v-icon
          v-if="showAlertBell && !savedSearchHasAlert"
          @click="showNotificationBuilder"
          class="pl-4"
          title="Setup notifications"
        >
          far fa-bell
        </v-icon>
        <v-icon
          v-if="showAlertBell && savedSearchHasAlert"
          @click="showNotificationBuilder"
          class="pl-4"
          title="Update notifications"
        >
          fas fa-bell
        </v-icon>
      </div>
      <div class="clearfix"></div>
    </div>
    <v-overlay :absolute="true" :value="screenShotting">
      <div class="copy-overlay-indicator">
        <v-icon size="50" color="white">fas fa-check</v-icon>
        <div><span class="application-heading">Copied</span></div>
      </div>
    </v-overlay>
    <div v-if="!isMinimized">
      <div class="hyp-header application-heading pa-2">
        <span class="results-header"
          >Results<span v-if="savedSearchDetails"> for:</span></span
        >
        <v-btn
          v-if="
            !isMinimized &&
            ((isEventsChartsUnlocked && isEventsSelected) ||
              isVerifiedEventsSelected)
          "
          @click="handleClickViewEventGraphs"
          depressed
          aria-describedby="charts-button-description"
          :aria-disabled="isChartsDisabled"
          class="charts-button mr-4 pa-1"
          :class="{
            disabled: isChartsDisabled,
          }"
        >
          <PopupInfo
            :text="chartsAltText"
            textId="charts-button-description"
            iconSize="1.5em"
            icon="mdi-chart-bar"
            className="pa-1"
            openDelay="1000ms"
          ></PopupInfo>
        </v-btn>

        <v-menu open-on-click offset-x right rounded="lg">
          <template v-slot:activator="{ on: menu }">
            <v-icon v-on="menu" class="menu-icon mt-2" size="20"
              >fa-solid fa-bars</v-icon
            >
          </template>

          <v-list>
            <v-list-item @click="handleCopyClick">
              <v-icon class="pr-2">fas fa-clipboard </v-icon>
              <v-list-item-title>
                Copy all events to Clipboard
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="hyp-header application-heading pa-2">
        <span class="search-query-name-display mr-1" v-if="savedSearchDetails"
          >{{ searchQueryName }}
        </span>
        <span
          class="search-query-name-display mr-1"
          v-if="searchLibraryItemName"
          >{{ searchLibraryItemName }}
        </span>
      </div>
      <div v-if="!shouldPullData & !loading" class="no-results-text pa-2">
        Please enter a keyword or specify a source or event filter.
      </div>
      <div v-if="hasDeepLink & startDate & endDate" class="small mx-2">
        The saved search results below are filtered by a start date of
        {{ startDate }} and end date of {{ endDate }} as per the email link.<br />
        <v-btn color="primary" x-small @click="handleResetDeepLink"
          >Reset</v-btn
        >
      </div>
      <div
        v-if="
          shouldPullData && !loading && (!items || (items && !items.length))
        "
        class="no-results-text pa-2"
      >
        No results found. Please alter your filters.
      </div>
      <Loader v-if="loading" text="" color="black"></Loader>
      <DetailsListCluster
        v-if="items && items.length"
        @get-data="getContent"
        theme="light"
        :items="items"
        :page="page"
        :pageSize="pageSize"
        :height-percent="95"
        :matomo-category="matomoCategory"
        :show-primary-topics="true"
        :show-thumbs-down="true"
        :get-threshold-type-function="getThresholdTypeFunction"
        :get-live-search-params-function="getParams"
        @loaded="loading = false"
        :scroll-to-top="scrollToTop"
      ></DetailsListCluster>
    </div>
  </div>
</template>

<script>
import { circle } from "@turf/turf";
import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";
import lodashForOwn from "lodash/forOwn";
import lodashHas from "lodash/has";
import lodashRemove from "lodash/remove";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getFormatted } from "@/common/date.utilities";

dayjs.extend(utc);

import { mapGetters } from "vuex";

import {
  MODULE_PREFIX_SEARCH,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_SAVED_SEARCH,
  MODULE_PREFIX_NOTIFICATIONS,
  MODULE_PREFIX_ASSETS,
} from "@/store";
import {
  GET_DETAILS_SIDEBAR,
  SAVE_PREFERENCES,
  GET_SAVED_SEARCH_DETAILS,
  GET_MODAL,
  GET_NOTIFICATION,
  GET_NOTIFICATIONS,
  GET_ASSETS_REFRESH_MAP,
} from "@/store/actions.type";
import {
  SET_SEARCH_BOUNDS,
  SET_PLACES_DETAILS,
  SET_CHARTS_DATA,
} from "@/store/mutations.type";
import { ENDPOINT_WOD_MAP } from "@/common/constants/api";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { nowUtc, getTodayStartEndDate } from "@/common/date.utilities";
import {
  getArticles,
  getFilterCounts,
  dataToCopy,
} from "@/services/search/wod.service";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import {
  MAIN_MAP_ID,
  MAP_ICON_NAMES,
  MAP_ICON_COLORS,
  LAYER_ID_ACETATE,
} from "@/common/constants/maps";
import {
  mapMarkerData,
  removeMapData,
  addMapData,
  mapPolygonData,
} from "@/common/maps/features";
import { getDefaultPolygonOptions } from "@/common/maps/styles";
import {
  getTopicsArray,
  getTopicKey,
  getPrimaryTopics,
  getTopicType,
  removeMissingTopics,
  getTopicsArraySpecial,
  getAllPrimaryTopics,
} from "@/services/events/topics.service";
import {
  getBoundsStringSWNE,
  getPrecision,
  filterFeaturesToMapExtent,
} from "@/common/maps/utilities";
import {
  hasTwitterAccess,
  hasEssentialsAccess,
  hasMaritimeAccess,
} from "@/services/user/user.service";
import { pageTrackerLogger } from "@/common/pageTracker";
import { EVENT_ACTIONS, SEARCH_CATEGORIES } from "@/common/constants/analytics";
import { SOCIAL_SOURCES } from "@/common/constants/wodSocialSources";
import { SAVED_SEARCH_BOUND_TYPES } from "@/common/constants/savedSearch";
import { httpGet } from "@/services/api.service";
import { getKeywordsStringFromBuilder } from "@/services/search/savedSearch.service";
import {
  NOTIFICATION_TYPE_SAVED_SEARCH,
  NOTIFICATION_ID_PROPERTY,
  NOTIFICATION_GLOBAL_SEARCHES_OWNER,
} from "@/common/constants/notifications";
import { formatNotification } from "@/services/notifications/notifications.service";
import { getChartData } from "@/services/charts/charts.service";
import { filterPrecisionIdsByEventType } from "@/common/constants/geolocationPrecisions.js";
import { getTagsConfig } from "@/services/search/tags.service";
import { MAP_TILE_SERVER_DYNAMIC } from "@/common/config";

const EMITTED_ACTIONS = {
  FILTER_COUNTS_UPDATE: "filter-counts-update",
  CLICK_MINIMIZE: "click-minimize",
  CLICK_CLOSE: "click-close",
  CHANGE_DEEP_LINK: "change-deep-link",
};

const distanceUnits = {
  mi: "miles",
  km: "kilometers",
};

const HEATMAP_LAYER_ID = "layer-search-heatmap";

export default {
  name: "SearchContent",
  components: {
    PopupInfo: () => import("@/components/common/PopupInfo"),
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    refresh: {
      type: String,
    },
    keywords: {
      type: String,
    },
    keywordsBuilder: {
      type: Object,
    },
    dateRange: {
      type: Object,
    },
    dateRangeStartDaysBack: {
      type: Number,
    },
    bounds: {
      type: String,
    },
    boundsType: {
      type: String,
      validator: function (value) {
        return SAVED_SEARCH_BOUND_TYPES.indexOf(value) !== -1;
      },
      default: "mapextent",
    },
    aoiId: {
      type: Array,
    },
    places: {
      type: Array,
    },
    assets: {
      type: Array,
    },
    radius: {
      type: [String, Number],
    },
    poisDistUnits: {
      type: [String],
    },
    events: {
      type: Array,
    },
    eventsV2: {
      type: Object,
    },
    hotspotsOnly: {
      type: Boolean,
      default: false,
    },
    sources: {
      type: Object,
    },
    isMinimized: {
      type: Boolean,
      default: false,
    },
    shouldPullData: {
      type: Boolean,
      default: false,
    },
    riskCategories: {
      type: String,
    },
    severity: {
      type: Array,
    },
    geolocationPrecisions: {
      type: Array,
    },
    hasDeepLink: {
      type: Boolean,
    },
    riskCategoryTags: {
      type: String,
    },
    sentiment: {
      type: Array,
    },
    emotions: {
      type: Array,
    },
    searchLibraryName: {
      type: String,
    },
  },
  data() {
    return {
      screenShotting: false,
      loading: false,
      features: null,
      featuresResults: null,
      items: null,
      itemsResults: null,
      page: 1,
      pageSize: 25,
      lastParams: null,
      requestTimestamp: new Date().toISOString(),
      currentType: null,
      clickBounds: null,
      filterCounts: null,
      layerRequestTimestamps: {},
      searchQueryName: null,
      scrollToTop: new Date().toISOString(),
      idsPulled: [],
      lastTimestamp: null,
      idsPulledDetails: [],
      lastTimestampDetails: null,
      start: lodashGet(this.$route, "query.start"),
      end: lodashGet(this.$route, "query.end"),
      matomoCategory: SEARCH_CATEGORIES.live_search,
      tagsList: [],
      assetsLayerId: "layer-assets",
      assetsBaseTileUrl: `${MAP_TILE_SERVER_DYNAMIC}assets/{z}/{x}/{y}.pbf`,
      searchLibraryItemName: this.searchLibraryName,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      maps: MODULE_PREFIX_MAP + "maps",
      refreshDataMap: MODULE_PREFIX_MAP + "refreshData",
      mainMapMoveEnd: MODULE_PREFIX_MAP + "mainMapMoveEnd",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      searchKeyword: MODULE_PREFIX_SEARCH + "keyword",
      searchDetails: MODULE_PREFIX_SEARCH + "searchDetails",
      savedSearchDetails: MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails",
      searchType: MODULE_PREFIX_SEARCH + "type",
      modal: MODULE_PREFIX_NOTIFICATIONS + "modal",
      translatedStore: MODULE_PREFIX_SEARCH + "translated",
      notifications: MODULE_PREFIX_NOTIFICATIONS + "notifications",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    isEventsChartsUnlocked() {
      return this.featureUnlocked("events-charts");
    },
    isSearchLibraryItem() {
      return (
        this.savedSearchDetails?.ownerId ===
          NOTIFICATION_GLOBAL_SEARCHES_OWNER ||
        this.savedSearchDetails?.userId === NOTIFICATION_GLOBAL_SEARCHES_OWNER
      );
    },
    isSentimentFilterUnlocked() {
      return this.featureUnlocked("livesearch-sentiment-filter");
    },
    isEmotionsFilterUnlocked() {
      return this.featureUnlocked("livesearch-emotion-filter");
    },
    /**
     * Determines whether the charts are disabled for the current search. This value can change as the search parameters and search state are changed.
     * @returns {boolean}
     */
    isChartsDisabled() {
      return this.loading || !this.items || !this.items.length;
    },
    /**
     * A description of the charts button to use in tooltips and accessibility
     * @returns {string}
     */
    chartsAltText() {
      if (this.loading) {
        return "Getting results...";
      } else if (!this.items?.length) {
        return "No results found to visualize";
      } else if (this.isChartsDisabled) {
        return "Visualizations are not available for this search";
      }

      return "Visualize search results";
    },
    savedSearchHasAlert() {
      let hasAlert = false;
      if (
        this.savedSearchDetails &&
        this.savedSearchDetails[NOTIFICATION_ID_PROPERTY]
      ) {
        if (this.featureUnlocked("saved-search-new-api")) {
          hasAlert = this.notifications.some(
            (o) =>
              o[NOTIFICATION_ID_PROPERTY] ===
              this.savedSearchDetails[NOTIFICATION_ID_PROPERTY]
          );
        } else {
          const savedSearch = lodashFind(
            this.$store.state.savedSearch.queries,
            [
              NOTIFICATION_ID_PROPERTY,
              this.savedSearchDetails[NOTIFICATION_ID_PROPERTY],
            ]
          );
          hasAlert =
            !!savedSearch?.formatted?.frequency || !!savedSearch?.frequency;
        }
      }
      return hasAlert;
    },
    startDate() {
      if (this.start) {
        return getFormatted(this.start);
      }

      return "None";
    },
    endDate() {
      if (this.end) {
        return getFormatted(this.end);
      }
      return "None";
    },
    /**
     * verifiedEventsSelected should be deprecated when the verified events chart feature lock is deprecated.
     * isEventsSelected replaces the purpose of this param
     */
    isVerifiedEventsSelected() {
      const sources = this.lastParams?.sources?.split(",");
      return (
        this.lastParams?.eventType === "verified" &&
        (sources?.includes("maritime") || sources?.includes("political"))
      );
    },
    isEventsSelected() {
      return this.lastParams?.sources !== "analysis" || false;
    },
    selectedEventType() {
      if (this.lastParams) {
        if (this.lastParams.verifiedType) {
          return this.lastParams.verifiedType;
        } else if (this.lastParams.hotspotsOnly === "true") {
          return "hotspots";
        } else if (this.lastParams.eventType) {
          return this.lastParams.eventType;
        } else {
          return "";
        }
      } else {
        return "";
      }
    },
    showAlertBell() {
      return (
        this.savedSearchDetails &&
        (this.savedSearchDetails?.userId === this.savedSearchDetails?.ownerId ||
          this.isSearchLibraryItem) &&
        !this.isMinimized
      );
    },
  },
  async mounted() {
    if (
      this.preferences?.liveSearch.searchLibraryName &&
      this.searchLibraryItemName === null
    ) {
      this.searchLibraryItemName = this.preferences?.liveSearch.searchLibraryName;
    }
    this.getTags();
    this.getNotifications();
  },
  methods: {
    async handleCopyClick() {
      const colsToCopy = [
        "title",
        "published",
        "occurred",
        "risks",
        "severity",
        "topic_scores_v2",
        "maritimeSources",
        "summary",
        "countries",
        "waters",
        "link",
      ];
      document.activeElement.blur();

      this.screenShotting = true;
      setTimeout(() => {
        this.screenShotting = false;
      }, 500);

      const items = lodashCloneDeep(this.items);
      await this.updateTranslatedItems(items);

      await dataToCopy(items, colsToCopy);
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search,
        EVENT_ACTIONS.clicked,
        "Copy All To Clipboard"
      );
    },
    async updateTranslatedItems(items) {
      const len = items.length;
      for (let i = 0; i < len; i++) {
        const item = items[i];
        if (
          lodashGet(item, "properties.id") &&
          lodashGet(item, "properties.formatted")
        ) {
          if (
            lodashGet(this.translatedStore, `["${item.properties.id}"].title`)
          ) {
            item.properties.formatted.title = this.translatedStore[
              item.properties.id
            ].title;
          }
          if (
            lodashGet(this.translatedStore, `["${item.properties.id}"].summary`)
          ) {
            item.properties.formatted.summary = this.translatedStore[
              item.properties.id
            ].summary;
          }
        }
      }
    },
    async getTags() {
      this.tagsList = await getTagsConfig();
    },
    async getNotifications() {
      await this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATIONS,
        {}
      );
    },
    handleResetDeepLink(e) {
      e.preventDefault();

      const clone = lodashCloneDeep(this.savedSearchDetails);

      clone.cachedOn = new Date().toISOString();
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
        clone
      );

      delete this.$route.query.start;
      delete this.$route.query.end;
      this.$emit(EMITTED_ACTIONS.CHANGE_DEEP_LINK, false);

      this.$router.push("/home");
    },
    handleClickClose(type) {
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE, type);
    },
    handleClickMinimize() {
      this.$emit(EMITTED_ACTIONS.CLICK_MINIMIZE);
    },
    async handleClickViewEventGraphs() {
      if (this.isChartsDisabled) {
        return;
      }

      if (this.detailsSidebar.visible === true) {
        this.detailsSidebar.visible = false;
      }

      const searchParams = this.getParams();

      this.$store.commit(MODULE_PREFIX_SEARCH + SET_CHARTS_DATA, {
        type: this.selectedEventType,
        data: {},
        searchParams: searchParams,
      });
      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
      this.openGraphSideBar();

      const chartData = await this.getChartData();
      this.$store.commit(MODULE_PREFIX_SEARCH + SET_CHARTS_DATA, {
        type: this.selectedEventType,
        data: chartData,
        searchParams: searchParams,
      });
      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
    openGraphSideBar() {
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.eventsCharts;
      this.detailsSidebar.visible = true;
    },
    async getChartData() {
      if (this.isChartsDisabled) {
        return {};
      }

      const params = this.getParams();

      return await getChartData(params);
    },
    showNotificationBuilder() {
      let notification;
      if (
        this.featureUnlocked("saved-search-new-api") &&
        this.savedSearchHasAlert
      ) {
        notification = this.notifications.find(
          (o) =>
            o[NOTIFICATION_ID_PROPERTY] ===
            this.savedSearchDetails[NOTIFICATION_ID_PROPERTY]
        );
      } else {
        notification = lodashCloneDeep(this.savedSearchDetails);
      }
      formatNotification(notification);
      this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION,
        notification
      );
      const modal = lodashCloneDeep(this.modal);
      modal.visible = true;
      modal.type = NOTIFICATION_TYPE_SAVED_SEARCH;
      modal.commsOnly = true;
      modal.newAlert =
        (this.featureUnlocked("saved-search-new-api") ||
          this.featureUnlocked("search-library")) &&
        !this.savedSearchHasAlert;
      this.$store.dispatch(MODULE_PREFIX_NOTIFICATIONS + GET_MODAL, modal);

      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_notification,
        EVENT_ACTIONS.clicked,
        "Bell to open saved search notifications"
      );
    },
    getFilterCounts(safeCounts) {
      this.filterCounts = safeCounts;
      this.$emit(EMITTED_ACTIONS.FILTER_COUNTS_UPDATE, safeCounts);
      this.getFilterCountsDebounced();
    },
    getFilterCountsDebounced: lodashDebounce(async function () {
      let params = this.getParams();
      params.getFilterCounts = true;
      params.pageOffset = 0;
      params.pageSize = 0;
      try {
        let result = await getFilterCounts(params, this.requestTimestamp);
        this.filterCounts = result;
        this.$emit(EMITTED_ACTIONS.FILTER_COUNTS_UPDATE, result);
      } catch (error) {
        console.error("Error in getFilterCounts: " + JSON.stringify(error));
        this.$emit(EMITTED_ACTIONS.FILTER_COUNTS_UPDATE, {});
      }
    }, 1000),
    async getData(params, page) {
      // this is used to get the data for the right slideout when user clicks on map icon
      params = params ? params : {};

      let result;
      try {
        page = !page ? 1 : page;
        const PAGE_OFFSET = (page - 1) * this.pageSize;

        params.pageSize = this.pageSize;
        params.pageOffset = PAGE_OFFSET;
        params.idsPulled = this.lastTimestampDetails
          ? this.idsPulledDetails
          : [];

        result = await getArticles(params, this.lastTimestampDetails);

        if (result && result.features) {
          if (
            this.featuresResults &&
            Array.isArray(this.featuresResults) &&
            this.featuresResults.length &&
            page !== 1 &&
            result.features.length
          ) {
            this.featuresResults = this.featuresResults.concat(result.features);
          } else {
            this.featuresResults = result.features;
          }
          this.features = lodashCloneDeep(this.featuresResults);
        } else {
          this.features = null;
          this.featuresResults = null;
        }

        this.idsPulledDetails = params.idsPulled;
        this.lastTimestampDetails = lodashGet(
          this,
          "features[0].properties.formatted.requestTimestamp"
        );
      } catch (error) {
        console.error("Error in getData: " + JSON.stringify(error));
      } finally {
        result = null;
      }
    },
    getContent(page, $state, params) {
      if (!this.mainMapMoveEnd) {
        return;
      }
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }
      this.loading = true;
      this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: lodashDebounce(async function (page, $state, params) {
      // this is used to get the data for the live search listing

      let result;
      let dataParams;
      try {
        this.loading = true;

        dataParams = this.getParams(params) || {};

        page = !page ? 1 : page;
        const PAGE_OFFSET = (page - 1) * this.pageSize;

        dataParams.pageSize = this.pageSize;
        dataParams.pageOffset = PAGE_OFFSET;

        let currParams = lodashCloneDeep(dataParams);
        if (currParams.hasOwnProperty("pageOffset")) {
          delete currParams.pageOffset;
        }

        let prevParams = lodashCloneDeep(this.lastParams);
        if (prevParams && prevParams.hasOwnProperty("pageOffset")) {
          delete prevParams.pageOffset;
        }
        if (!lodashIsEqual(currParams, prevParams)) {
          this.requestTimestamp = new Date().toISOString();
        }

        this.lastParams = dataParams;
        this.setPrefs(dataParams);

        if (this.shouldPullData) {
          // We don't currently map analysis so don't make add'l calls
          const _sources = dataParams.sources.split(",");
          if (
            (_sources &&
              _sources.length === 1 &&
              _sources.includes("analysis")) ||
            (this.riskCategories && this.riskCategories.length > 0)
          ) {
            this.clearMap();
          } else {
            this.addDataToMap();
          }
          dataParams.idsPulled = this.lastTimestamp ? this.idsPulled : [];

          const articles = await getArticles(dataParams, this.lastTimestamp);
          // Extra check in case results took long and this.shouldPullData value is false by the time pending response comes back
          if (this.shouldPullData) {
            result = articles;
          }
        } else {
          result = { features: [] };
          this.clearMap();
        }

        if (result && result.features) {
          if (
            this.itemsResults &&
            Array.isArray(this.itemsResults) &&
            this.itemsResults.length &&
            page !== 1
          ) {
            this.itemsResults = this.itemsResults.concat(result.features);
          } else {
            this.itemsResults = result.features;
          }
          this.items = lodashCloneDeep(this.itemsResults);

          this.idsPulled = dataParams.idsPulled;
          this.lastTimestamp = lodashGet(
            result,
            "features[0].properties.formatted.requestTimestamp"
          );

          if (this.keywords && this.keywords.length > 0) {
            _paq.push([
              function () {
                let userId = this.getUserId();
                if (userId.length === 0) {
                  pageTrackerLogger(
                    "The userId was not found when attempting to store the live track site search."
                  );
                  const profile = JSON.parse(
                    localStorage.getItem("user_profile")
                  );
                  if (profile && profile.email) {
                    _paq.push(["setUserId", profile.email]);
                    pageTrackerLogger(
                      "The userId " +
                        profile.email +
                        " will be set before the event."
                    );
                  }
                }
              },
            ]);
            this.handlePageTrackerSiteSearch(
              dataParams.search,
              SEARCH_CATEGORIES.live_search,
              result.metadata ? result.metadata.total : 0
            );
          }
        } else {
          this.itemsResults = null;
          this.featuresResults = null;
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        if (result) {
          setInfiniteScrollState(
            $state,
            { features: result.features },
            "features"
          );
        }
        this.page = page;
        this.loading = false;
        result = null;
        dataParams = {};
      }
    }, 100),
    getThresholdTypeFunction(doc) {
      let thresholdType = "cluster";
      if (
        lodashGet(doc, "properties.formatted.source") === "twitter.com" ||
        lodashGet(doc, "properties.formatted.source") === "twitter"
      ) {
        thresholdType = "twitter";
      }
      return thresholdType;
    },
    getParams(params, type) {
      if (!params) {
        params = {};
      }

      /* get query string timestampProperties from deeplink alerts email */
      const timestampProperties = lodashGet(
        this.$route,
        "query.timestampProperties"
      );

      if (timestampProperties) {
        params.timestampProperties = timestampProperties;
      }

      /* get query string eventtimeStart from deeplink alerts email */
      const eventtimeStart = lodashGet(this.$route, "query.eventtimeStart");

      if (eventtimeStart) {
        params.eventtimeStart = eventtimeStart;
      }

      if (this.keywordsBuilder) {
        const keywordsBuilderString = getKeywordsStringFromBuilder(
          this.keywordsBuilder
        );
        if (keywordsBuilderString) {
          params.keywordsBuilder = this.keywordsBuilder;
          params.search = keywordsBuilderString;
        } else if (this.keywords) {
          params.search = this.keywords;
        }
      } else if (this.keywords) {
        params.search = this.keywords;
      }

      params.start = getTodayStartEndDate(
        dayjs(nowUtc()).subtract(this.dateRangeStartDaysBack, "day")
      ).start;
      params.end = getTodayStartEndDate(dayjs(nowUtc())).end;
      if (lodashGet(this.dateRange, "start")) {
        params.start = this.dateRange.start;
        params.end = this.dateRange.end ? this.dateRange.end : null;
      }

      //Bounds
      params.boundsType = this.boundsType ? this.boundsType : "geography";
      params.bounds = this.bounds;
      if (
        params.boundsType === "worldwide" ||
        params.boundsType === "geography"
      ) {
        delete params.bounds;
      } else if (params.boundsType === "mapextent" && this.map) {
        params.bounds = getBoundsStringSWNE(this.map);
      } else {
        params.bounds = "-90,-180,90,180";
      }

      if (this.clickBounds) {
        params.bounds = this.clickBounds;
      }

      if (this.aoiId && this.aoiId.length) {
        params.aoiId = this.aoiId.join();
      }

      let pois = [];
      if (this.places && this.places.length > 0 && this.radius) {
        params.poisDistUnits = this.poisDistUnits;
        params.radius = this.radius;
        pois = this.getPlacesCoordinates() || [];
        if (pois) {
          pois = pois.map((poi) => {
            poi[2] = this.radius;
            return poi;
          });
          params.pois = JSON.stringify(pois);
        }
        delete params.bounds; // AAAS-5067
      } else if (this.assets && this.assets.length > 0 && this.radius) {
        params.poisDistUnits = this.poisDistUnits;
        params.radius = this.radius;
        const coords = this.getAssetsCoordinates(false, true) || [];
        if (coords) {
          coords.forEach((item) => {
            pois.push([item.coords[0], item.coords[1], this.radius, item.id]);
          });
          params.pois = JSON.stringify(pois);
        }
        delete params.bounds; // AAAS-5067
      }

      if (this.events) {
        params.topics = this.events.join();
      }

      if (this.hotspotsOnly) {
        params.hotspotsOnly = this.hotspotsOnly.toString();
      }

      if (this.riskCategories) {
        params.riskCategories = this.riskCategories;
      }

      if (this.severity) {
        params.severityMin = this.severity[0];
        params.severityMax = this.severity[1];
      }

      if (this.riskCategoryTags) {
        params.tags = this.riskCategoryTags;

        let tags = this.riskCategoryTags.split(",");
        let globalTagIds = this.tagsList
          .filter((tag) => {
            if (tag.isGlobal) {
              return tag;
            }
          })
          .map((t) => {
            return t.id;
          });

        if (params.bounds && tags.some((tag) => globalTagIds.includes(tag))) {
          delete params.bounds;
        }
      }

      if (this.currentType) {
        if (
          this.currentType === "news" ||
          this.currentType === "social" ||
          lodashHas(this.eventsV2, "eventType")
        ) {
          if (!type || !Array.isArray(type)) {
            type = [];
          }
          type.push(this.currentType);
        } else {
          params.topics = this.getSidebarTopics(this.currentType).join();
        }
      }

      if (type && Array.isArray(type) && type[0].includes("topic-")) {
        let topics = [];
        type.forEach((t) => {
          const event = t.split("topic-");
          if (event && Array.isArray(event) && event.length > 1) {
            topics.push(event[1]);
          }
        });
        params.topics = topics.join();
      }

      if (type && Array.isArray(type) && type[0] === "news") {
        params.sources = "news";
      } else if (type && Array.isArray(type) && type[0] === "social") {
        params.sources = SOCIAL_SOURCES.join(",");
      } else if (type && Array.isArray(type) && type[0] === "incidents") {
        params.sources =
          this.eventsV2?.eventType === "all" && hasMaritimeAccess()
            ? "political,maritime"
            : this.eventsV2?.eventType === "all" ||
              this.eventsV2?.verifiedType === "security"
            ? "political"
            : this.eventsV2?.verifiedType === "maritime" && hasMaritimeAccess()
            ? "maritime"
            : "political";
      } else {
        if (
          (this.riskCategories || this.riskCategoryTags) &&
          (!this.eventsV2.events || this.eventsV2.events === "")
        ) {
          params.sources = "analysis";
        } else if (this.eventsV2.eventType === "all") {
          let sources = [];
          if (this.sources && (this.sources.news || this.sources.social)) {
            if (this.sources.news) {
              sources.push("news");
            }
            if (hasTwitterAccess() && this.sources.social) {
              sources.push(SOCIAL_SOURCES);
            }
          } else {
            sources.push("news", SOCIAL_SOURCES);
          }

          sources.push("political");

          if (hasMaritimeAccess()) {
            sources.push("maritime");
          }

          params.sources = sources.join();
        } else if (this.eventsV2.eventType === "verified") {
          params.sources =
            this.eventsV2.verifiedType === "maritime" && hasMaritimeAccess()
              ? "maritime"
              : "political";
        } else {
          // Default behavior
          let sources = [];
          if (this.hotspotsOnly) {
            sources.push("news");
          }

          if (this.sources && !this.hotspotsOnly) {
            if (this.sources.news) {
              sources.push("news");
            }
            if (hasTwitterAccess() && this.sources.social) {
              sources.push(SOCIAL_SOURCES);
            }
          }
          params.sources = sources.join();
        }
      }

      if (params.sources === "social") {
        params.sources = "twitter";
      }

      if (this.eventsV2) {
        if (lodashGet(this.eventsV2, "eventType")) {
          params.eventType = lodashGet(this.eventsV2, "eventType") || null;
        }

        if (lodashGet(this.eventsV2, "mapType")) {
          params.mapType = lodashGet(this.eventsV2, "mapType") || null;
        }

        if (type && type.includes("incidents")) {
          params.topics = type[1];
        } else if (!type) {
          params.topics = this.eventsV2.events;
        }

        if (lodashGet(this.eventsV2, "eventType") === "verified") {
          params.verifiedType =
            lodashGet(this.eventsV2, "verifiedType") || "security";
          params.sectors = lodashGet(this.eventsV2, "sectors") || null;
          params.assets = lodashGet(this.eventsV2, "assets") || null;
          params.attackTypes = lodashGet(this.eventsV2, "attackTypes") || null;
          params.perpetrators =
            lodashGet(this.eventsV2, "perpetrators") || null;
          params.maritimeSubcategories =
            lodashGet(this.eventsV2, "maritimeSubcategories") || null;
          params.maritimeVesselTypes =
            lodashGet(this.eventsV2, "maritimeVesselTypes") || null;
          const severity = lodashHas(this.eventsV2, "severity")
            ? lodashGet(this.eventsV2, "severity")
            : null;
          if (severity && params.verifiedType !== "maritime") {
            params.severityMin = severity[0];
            params.severityMax = severity[1];
          }
        } else {
          // how is backend going to differienate between detected and all?
          // if all subtopics in a topic are selected then just send the primary topic
          // but if not all subtopics are selected then we send each of the selected subtopics
          // this should be exactly like how current events are passed using params.topics
          if (this.eventsV2.events && this.eventsV2.events.length) {
            if (!type) {
              params.topics = this.eventsV2.events;
            } else {
              let typeArr = lodashCloneDeep(type);
              typeArr = lodashRemove(typeArr, (n) => {
                return n !== "news" && n !== "social";
              });
              typeArr = typeArr.map((n) => {
                let evt = n.replace("topic-", "");
                evt = getTopicKey(evt, true);
                return evt;
              });
              params.topics = typeArr;
            }
          }
        }
      }

      //eventsV2
      //{eventType: 'verified', verifiedType: 'security', events: 'disaster,unrest,war'}
      // if(this.eventsV2.eventType && )
      // Only send the correct topics based on the eventType to the wod api
      if (params.topics) {
        //TODO don't love that the type here is inconsistent. figure out why its doing this....
        let topicsArray =
          typeof params.topics === "string"
            ? params.topics.split(",")
            : params.topics;
        params.topics = removeMissingTopics(
          topicsArray,
          getTopicsArraySpecial(params.eventType, params.verifiedType),
          "apiName"
        ).join(",");
      }

      if (this.geolocationPrecisions) {
        let geolocationPrecisionType = "all";
        if (this.eventsV2) {
          if (this.eventsV2.verifiedType) {
            geolocationPrecisionType = this.eventsV2.verifiedType;
          } else if (this.eventsV2.eventType) {
            geolocationPrecisionType = this.eventsV2.eventType;
          }
        }

        let relevantPrecisions = filterPrecisionIdsByEventType(
          geolocationPrecisionType,
          this.geolocationPrecisions
        );

        if (relevantPrecisions.length > 0) {
          params.geolocationPrecisions = relevantPrecisions;
        }
      }

      // Events and Analysis are mutually exclusive. If an Analysis Risk Category is selected, turn off
      // all incompatible Event filters.
      if (this.riskCategories) {
        params.riskCategories = this.riskCategories;
        params.sources = "analysis";
        delete params.hotspotsOnly;
        delete params.eventType;
        delete params.mapType;
        delete params.topics;
        delete params.verifiedType;
        delete params.severityMax;
        delete params.severityMin;
        delete params.sectors;
        delete params.assets;
        delete params.attackTypes;
        delete params.perpetrators;
        delete params.geolocationPrecisions;
      }

      if (this.riskCategoryTags) {
        params.tags = this.riskCategoryTags;
        params.sources = "analysis";
      }

      if (
        this.isSentimentFilterUnlocked &&
        this.sentiment?.length &&
        (params.eventType === "all" || params.eventType === "detected") &&
        params.sources !== "analysis"
      ) {
        params.sentiments = this.sentiment.join(",");
      }

      if (
        this.isEmotionsFilterUnlocked &&
        this.emotions?.length &&
        (params.eventType === "all" || params.eventType === "detected") &&
        params.sources !== "analysis"
      ) {
        params.emotions = this.emotions.join(",");
      }

      return params;
    },
    getTopicsAndSubtopics(events) {
      let evts = [];

      if (events && events.length) {
        const TOPICS_ARRAY = this.eventsV2
          ? getPrimaryTopics(this.eventsV2.eventType)
          : getPrimaryTopics("detected");
        events.forEach((event) => {
          const topic = lodashFind(TOPICS_ARRAY, { apiName: event });
          if (topic) {
            let topicEvts = [];
            if (topic.subtopics && topic.subtopics.length) {
              topic.subtopics.forEach((s) => {
                if (!s.hidden) {
                  topicEvts.push(s.apiName);
                }
              });
            }
            if (!topicEvts.length) {
              topicEvts.push(topic.apiName);
            }
            evts = evts.concat(topicEvts);
          }
        });
      }
      return evts;
    },
    getEventsV2asArray2() {
      let eventNames = []; //type or apiName?

      //TODO why do we care about flipping eventType again?
      if (
        lodashGet(this.eventsV2, "eventType") &&
        lodashGet(this.eventsV2, "events") &&
        this.eventsV2.events.length
      ) {
        if (typeof this.eventsV2.events === "string") {
          eventNames = this.eventsV2.events.split(",");
        } else {
          if (typeof this.eventsV2.events[0] === "string") {
            eventNames = this.eventsV2.events.join();
          } else {
            this.eventsV2.events.forEach((evt) => {
              eventNames.push(evt.apiName);
            });
          }
        }

        const TOPICS_ARRAY = getTopicsArray();

        //TODO this doesn't roll up subtopics when they're all selected but the primary isn't

        TOPICS_ARRAY.forEach((curTopic) => {
          if (curTopic.parentIds && eventNames.indexOf(curTopic.apiName) > -1) {
            curTopic.parentIds.forEach((pId) => {
              if (eventNames.indexOf(pId) > -1) {
                let remIndex = eventNames.indexOf(curTopic.apiName);
                eventNames.splice(remIndex, 1);
              }
            });
          }
        });
      }
      return eventNames;
    },
    getEventsV2asArray() {
      return this.getEventsV2asArray2();
      //TODO: This should be cleaned up as tech debt
      const events = [];
      if (
        lodashGet(this.eventsV2, "eventType") &&
        lodashGet(this.eventsV2, "events") &&
        this.eventsV2.events.length
      ) {
        let stats = {};
        this.eventsV2.events.forEach((evt) => {
          if (evt.parent) {
            if (!lodashHas(stats, evt.parent)) {
              stats[evt.parent] = 1;
            } else {
              stats[evt.parent] += 1;
            }
          } else {
            if (!lodashHas(stats, evt.type)) {
              stats[evt.apiName] = 0;
            }
          }
        });

        let pTopics;
        if (this.eventsV2.eventType === "verified") {
          // Do verified events even attempt to be added to the map?
          pTopics = getPrimaryTopics("verified");
          pTopics.forEach((t) => {
            this.eventsV2.events.forEach((evt) => {
              if (evt.type === t.apiName) {
                events.push(t.apiName);
              }
            });
          });
        } else {
          pTopics = getPrimaryTopics("detected");
          pTopics.forEach((t) => {
            if (lodashHas(stats, t.apiName)) {
              if (stats[t.apiName] > 0) {
                if (t.subtopics && t.subtopics.length > stats[t.apiName]) {
                  this.eventsV2.events.forEach((evt) => {
                    if (evt.parent === t.apiName) {
                      events.push(evt.type);
                    }
                  });
                } else {
                  events.push(t.apiName);
                }
              } else {
                events.push(t.apiName);
              }
            }
          });
        }
      }
      return events;
    },
    getMapEndpoint(type) {
      const params = this.getParams(null, type);
      params.precision = getPrecision(this.map);
      params.pageSize = 10000;
      return encodeAllURLParameters({
        baseEndpoint: ENDPOINT_WOD_MAP,
        params: params,
      });
    },
    clearMap: function () {
      this.removeDataFromMap("news");
      this.removeDataFromMap("social");
      const TOPICS_ARRAY = getTopicsArray();
      for (let i = 0; i < TOPICS_ARRAY.length; i++) {
        const topic = TOPICS_ARRAY[i];
        if (!topic.parentIds) {
          this.removeDataFromMap("topic-" + getTopicKey(topic.apiName));
        }
      }

      removeMapData(this.map, HEATMAP_LAYER_ID);
      removeMapData(this.map, "placebuffers");
      removeMapData(this.map, "placesselected");
      removeMapData(this.map, "assetbuffers");
    },
    async addLayerToMap(layerOptions) {
      try {
        layerOptions["metadata"] = "seerist-points-searches";
        let timestamp = new Date().toISOString();
        this.layerRequestTimestamps[layerOptions.id] = timestamp;
        const RESULT = await httpGet({
          url: layerOptions.aggData,
        });
        const { data } = JSON.parse(RESULT);
        layerOptions.data = data;

        if (
          timestamp !== this.layerRequestTimestamps[layerOptions.id] ||
          !this.shouldPullData
        ) {
          return;
        }

        addMapData(layerOptions);
      } catch (error) {
        console.error("Failed to retrieve data for layer");
        console.error(error);
      }
    },
    addDataToMap: lodashDebounce(function () {
      this.clearMap();

      let iEvents = this.events;
      if (this.eventsV2) {
        iEvents = this.getEventsV2asArray();
      }

      // determine which events to map
      let events = [];
      if (iEvents && iEvents.length) {
        const TOPICS_ARRAY = getTopicsArray();
        TOPICS_ARRAY.forEach((topic) => {
          if (iEvents.includes(topic.apiName)) {
            if (
              this.eventsV2.eventType === "verified" &&
              topic.supportsVerified
            ) {
              if (
                !this.eventsV2.verifiedType ||
                (this.eventsV2.verifiedType === "security" &&
                  topic.supportsPolitical) ||
                (this.eventsV2.verifiedType === "maritime" &&
                  topic.supportsMaritime &&
                  hasMaritimeAccess())
              ) {
                events.push(topic);
              }
            } else if (
              this.eventsV2.eventType === "detected" &&
              topic.supportsDetected
            ) {
              events.push(topic);
            } else if (this.eventsV2.eventType === "all") {
              events.push(topic);
            }
          }
        });
      }

      //When searching for verified events without any specific topics or type selected, add a layer for every type and topic on the map.
      if (!events?.length && this.eventsV2.eventType === "verified") {
        if (
          !this.eventsV2.verifiedType ||
          this.eventsV2.verifiedType === "security"
        ) {
          //Map security/political VEs by default
          events = getTopicsArray().filter(
            (topic) =>
              topic.supportsVerified && topic.supportsPolitical && topic.apiName
          );
        } else if (
          this.eventsV2.verifiedType === "maritime" &&
          hasMaritimeAccess()
        ) {
          events = getTopicsArray().filter(
            (topic) =>
              topic.supportsVerified && topic.supportsMaritime && topic.apiName
          );
        }
      }

      // add the event layers
      let eventLayers = {};
      for (let i = 0; i < events.length; i++) {
        const topic = events[i];
        let topType = getTopicType(topic);
        const topicKey = getTopicKey(topType);
        // need to check for parents, if same parent then put in same grouping
        if (!eventLayers[topicKey] && !!topicKey) {
          eventLayers[topicKey] = [];
        }
        if (topic.parentIds) {
          eventLayers[topicKey].push(`topic-${topic.apiName}`);
        }
      }

      if (this.eventsV2 && this.eventsV2.mapType === "heatmap") {
        this.addHeatmapToMap(eventLayers, iEvents);
      } else {
        this.addMarkersToMap(eventLayers, iEvents);
      }
      this.addPlacesToMap();
      this.addAssetsToMap();
    }, 2000),
    addMarkersToMap(eventLayers, iEvents) {
      let endpoint;

      if (eventLayers) {
        lodashForOwn(eventLayers, (value, key) => {
          let t = [];
          if (Array.isArray(value) && value.length) {
            t = value;
          } else {
            t.push(`topic-${key}`);
          }
          endpoint = this.getMapEndpoint(t);
          this.addLayerToMap({
            id: `layer-topic-${key}`,
            aggData: endpoint,
            map: this.map,
            mapClickEvent: this.mapClickEvent,
            clusterMapClickEvent: this.mapClickEvent,
            iconName: MAP_ICON_NAMES[key],
          });
          this.setMapClearButtonSelected();
        });
      }

      // uncategorized news layer
      if (
        iEvents.length === 0 &&
        this.eventsV2.eventType !== "verified" &&
        !hasEssentialsAccess() &&
        (!this.sources ||
          (this.sources &&
            (lodashGet(this.sources, "news") ||
              (!this.sources.news && !this.sources.social))))
      ) {
        endpoint = this.getMapEndpoint(["news"]);
        this.addLayerToMap({
          id: `layer-news`,
          aggData: endpoint,
          map: this.map,
          mapClickEvent: this.mapClickEvent,
          clusterMapClickEvent: this.mapClickEvent,
          iconName: MAP_ICON_NAMES["news"],
        });
      }

      // uncategorized twitter layer
      if (
        iEvents.length === 0 &&
        this.eventsV2.eventType !== "verified" &&
        hasTwitterAccess() &&
        !hasEssentialsAccess() &&
        (!this.sources ||
          (this.sources &&
            (lodashGet(this.sources, "social") ||
              (!this.sources.news && !this.sources.social))))
      ) {
        endpoint = this.getMapEndpoint(["social"]);
        this.addLayerToMap({
          id: `layer-social`,
          aggData: endpoint,
          map: this.map,
          mapClickEvent: this.mapClickEvent,
          clusterMapClickEvent: this.mapClickEvent,
          iconName: MAP_ICON_NAMES["social"],
        });
      }
    },
    addHeatmapToMap(eventLayers) {
      let endpoint = this.getMapEndpoint();
      if (eventLayers) {
        let topicsToRequest = [];
        lodashForOwn(eventLayers, (value, key) => {
          if (Array.isArray(value) && value.length) {
            topicsToRequest = topicsToRequest.concat(value);
          } else {
            topicsToRequest.push(key);
          }
        });
        if (topicsToRequest.length > 0) {
          endpoint = this.getMapEndpoint();
        }
      }

      this.addLayerToMap({
        id: HEATMAP_LAYER_ID,
        aggData: endpoint,
        map: this.map,
        mapClickEvent: this.mapClickEvent,
        clusterMapClickEvent: this.mapClickEvent,
        heatmap: true,
        beforeLayer: LAYER_ID_ACETATE,
      });
      this.setMapClearButtonSelected();
    },
    addPlacesToMap() {
      if (this.places && this.places.length) {
        this.addPlaceBuffersToMap();
        this.addPlaceIconsToMap();
      }
    },
    addPlaceIconsToMap() {
      const LAYER_ID = "placesselected";
      const SOURCE = {
        type: "FeatureCollection",
        features: this.places,
      };

      const OPTIONS = {
        markerLayerOptions: {
          id: LAYER_ID + "-marker",
          type: "symbol",
          source: LAYER_ID,
          filter: ["!has", "point_count"],
          layout: {
            "icon-allow-overlap": true,
            "icon-image": ["get", "map_icon_name"],
            "icon-size": 0.65,
          },
        },
      };

      mapMarkerData({
        id: LAYER_ID,
        data: SOURCE,
        mapClickEvent: this.placesMapClickEvent,
        markerOptions: OPTIONS,
        map: this.map,
        noCluster: true,
      });
    },
    async placesMapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.places;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.places;

        const PARAMS = {
          title: features[0].properties.map_layer_name
            ? features[0].properties.map_layer_name
            : "",
          feature: features[0],
        };
        this.$store.commit(MODULE_PREFIX_PLACES + SET_PLACES_DETAILS, PARAMS);

        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );
      }
    },
    addPlaceBuffersToMap() {
      let features = [];
      const coordsArr = this.getPlacesCoordinates();
      if (coordsArr && coordsArr.length) {
        coordsArr.forEach((coords) => {
          const feature = circle(coords, this.radius, {
            steps: 64,
            units: distanceUnits[this.poisDistUnits],
          });
          features.push(feature);
        });

        const SOURCE = {
          type: "FeatureCollection",
          features: features,
        };

        const PARAMS = {
          id: "placebuffers",
          opacity: 0.3,
        };
        const options = getDefaultPolygonOptions(PARAMS);
        options.polygonLayerOptions.hypDisableHover = true;

        mapPolygonData({
          id: PARAMS.id,
          data: SOURCE,
          polygonOptions: options,
          map: this.map,
        });
      }
    },
    addAssetsToMap() {
      if (this.assets && this.assets.length) {
        this.addAssetBuffersToMap();
        this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP, {
          enabled: true,
        });
      }
    },
    addAssetBuffersToMap() {
      let features = [];
      const coordsArr = this.getAssetsCoordinates();
      if (coordsArr && coordsArr.length) {
        coordsArr.forEach((coords) => {
          const feature = circle(coords, this.radius, {
            steps: 64,
            units: distanceUnits[this.poisDistUnits],
          });
          features.push(feature);
        });

        const SOURCE = {
          type: "FeatureCollection",
          features: features,
        };

        const PARAMS = {
          id: "assetbuffers",
          opacity: 0.3,
        };
        const options = getDefaultPolygonOptions(PARAMS);
        options.polygonLayerOptions.hypDisableHover = true;

        mapPolygonData({
          id: PARAMS.id,
          data: SOURCE,
          polygonOptions: options,
          map: this.map,
        });
      }
    },
    getPlacesCoordinates(onlyVisible = false) {
      let places = [];
      if (this.places && this.places.length) {
        const len = this.places.length;
        for (let i = 0; i < len; i++) {
          const place = this.places[i];
          if (lodashGet(place, "geometry.coordinates")) {
            places.push(place);
          }
        }
      }

      if (onlyVisible) {
        places = filterFeaturesToMapExtent(
          this.map,
          places,
          this.radius,
          distanceUnits[this.poisDistUnits]
        );
      }
      places = places.map((place) => {
        return place.geometry.coordinates;
      });
      return places.length ? places : null;
    },
    getAssetsCoordinates(onlyVisible = false, withIds = false) {
      let assets = lodashCloneDeep(this.assets);
      if (onlyVisible) {
        const assetFeatures = this.assets.map((asset) => {
          return {
            type: "Point",
            coordinates: [asset.longitude, asset.latitude],
          };
        });
        assets = filterFeaturesToMapExtent(
          this.map,
          assetFeatures,
          this.radius,
          distanceUnits[this.poisDistUnits]
        );
      }
      assets = assets.map((asset) => {
        let coords = [asset.longitude, asset.latitude];
        return !withIds
          ? coords
          : {
              id: asset.id,
              coords: coords,
            };
      });
      return assets.length ? assets : null;
    },
    getAssetsIds() {
      const assets = this.assets?.map((asset) => asset.id);
      return assets?.length ? assets : null;
    },
    getPlacesIds() {
      const places = [];
      if (this.places && this.places.length) {
        const len = this.places.length;
        for (let i = 0; i < len; i++) {
          const place = this.places[i];
          if (lodashGet(place, "id")) {
            places.push(place.id);
          }
        }
      }
      return places.length ? places : null;
    },
    removeDataFromMap(type) {
      this.layerRequestTimestamps[`layer-${type}`] = new Date().toISOString();
      if (this.map) {
        if (!type) {
          console.error(`Unable to remove ${type} from map.`);
          return;
        }
        removeMapData(this.map, `layer-${type}`);
      }
    },
    mapClickEvent(e, bounds) {
      e.originalEvent.stopPropagation();

      this.currentType = e.eventLayers[0]
        .replace("layer-", "")
        .replace("topic-", "")
        .replace("-geohash", "")
        .replace("-cluster", "");

      this.aggregateBounds = bounds;
      this.clickBounds =
        bounds[1] + "," + bounds[0] + "," + bounds[3] + "," + bounds[2];

      this.$store.commit(
        MODULE_PREFIX_SEARCH + SET_SEARCH_BOUNDS,
        this.clickBounds
      );
      this.showSidebar();
    },
    async showSidebar(params) {
      let showTime = new Date().getTime();
      params = params ? params : {};
      params.showTime = showTime;
      await this.setSidebar({ initial: true, showTime: showTime });
      await this.setSidebar(params);
    },
    getSidebarTopics(topicName, eventType) {
      let currentTopic;
      const TOPICS_ARRAY = getTopicsArray(eventType);
      for (let i = 0; i < TOPICS_ARRAY.length; i++) {
        if (
          TOPICS_ARRAY[i].apiName === topicName ||
          TOPICS_ARRAY[i].type === topicName
        ) {
          currentTopic = TOPICS_ARRAY[i];
        }
      }

      let res = [];
      for (let i = 0; i < TOPICS_ARRAY.length; i++) {
        let cur = TOPICS_ARRAY[i];
        if (
          ((cur.parentIds && currentTopic.apiName === cur.parentIds[0]) ||
            currentTopic.apiName === cur.apiName) &&
          lodashGet(this.eventsV2, "events") &&
          this.eventsV2.events.indexOf(cur.apiName) > -1
        ) {
          res.push(cur.apiName);
        }
      }
      if (res.length > 0) {
        return res;
      } else {
        return null;
      }
    },
    async setSidebar(params) {
      //if a more recent click has updated sidebar please stop
      if (this.$store.state.home.detailsSidebar.showTime > params.showTime) {
        return null;
      }

      if (this.currentType) {
        let title = this.currentType;
        let type = this.currentType;
        const TOPICS_ARRAY = getAllPrimaryTopics();
        const topic = lodashFind(TOPICS_ARRAY, {
          apiName: this.currentType,
        });
        if (topic) {
          title = topic.plural;
          type = topic.apiName;
          if (topic.parentIds && topic.parentIds[0]) {
            const parent = lodashFind(TOPICS_ARRAY, {
              apiName: topic.parentIds[0],
            });
            if (parent) {
              type = parent.apiName;
              title = `${parent.plural} > ${topic.plural}`;
            }
          }
        }

        this.detailsSidebar.loading = false;
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.mapClick;
        this.detailsSidebar.type = type;

        this.detailsSidebar.title = title;
        this.detailsSidebar.features = [];
        this.detailsSidebar.total = null;

        this.detailsSidebar.iconColor = MAP_ICON_COLORS[type];
        this.detailsSidebar.iconName = MAP_ICON_NAMES[type];

        this.detailsSidebar.hideSearchBar = true;

        if (params.initial) {
          this.detailsSidebar.loading = true;
          this.detailsSidebar.features = null;
          this.detailsSidebar.total = null;
          this.detailsSidebar.keywords = null;
          this.selectedKeywords = [];
        }

        if (params.showTime) {
          this.detailsSidebar.showTime = params.showTime;
        }

        this.detailsSidebar.SelectorSourceRef = this;

        this.detailsSidebar.getParamsFunction = this.getParams;

        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );
      }
    },
    setPrefs(params) {
      const inParams = lodashCloneDeep(params);
      delete inParams.pageOffset;
      delete inParams.pageSize;

      if (!inParams.hasOwnProperty("search")) {
        inParams.search = null;
      }

      if (!inParams.hasOwnProperty("keywordsBuilder")) {
        inParams.keywordsBuilder = null;
      } else {
        inParams.search = null;
      }

      if (
        !inParams.hasOwnProperty("topic") ||
        inParams.topic.length === 0 ||
        this.riskCategories
      ) {
        inParams.topic = null;
      }
      if (
        !inParams.hasOwnProperty("sources") ||
        inParams.sources.length === 0
      ) {
        inParams.sources = null;
      }
      inParams.aoiId = this.aoiId;
      inParams.placesRadius = this.radius;
      inParams.placesPoisDistUnits = this.poisDistUnits;
      inParams.places = this.getPlacesIds();
      inParams.assets = this.getAssetsIds();
      if (inParams.assets) {
        inParams.places = null;
      }
      inParams.riskCategories = this.riskCategories;
      inParams.tags = this.riskCategoryTags;
      inParams.geolocationPrecisions = this.geolocationPrecisions;
      inParams.sentiment = this.sentiment;
      inParams.emotions = this.emotions;
      inParams.searchLibraryName = this.searchLibraryItemName;

      if (this.eventsV2 && this.eventsV2.mapType && !this.riskCategories) {
        inParams.mapType = this.eventsV2.mapType;
      }

      if (this.preferences) {
        this.preferences.liveSearch = inParams;
        if (this.savedSearchDetails) {
          this.preferences.liveSearch.selectedSavedSearch = this.savedSearchDetails[
            NOTIFICATION_ID_PROPERTY
          ];
        }
        if (
          (inParams.end &&
            inParams.end === getTodayStartEndDate(dayjs(nowUtc())).end) ||
          inParams.end === null
        ) {
          let start = dayjs(inParams.start);
          let end = inParams.end
            ? dayjs(inParams.end)
            : getTodayStartEndDate(dayjs(nowUtc())).end;
          this.preferences.liveSearch.time_range = start.diff(end, "days");
        } else {
          delete this.preferences.liveSearch.time_range;
        }
        this.$store.dispatch(
          MODULE_PREFIX_USER + SAVE_PREFERENCES,
          this.preferences
        );
      }
    },
    handlePageTrackerSiteSearch(keyword, category, total) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackSiteSearch", keyword, category, total]);
      pageTrackerLogger(
        "Track Site Search " +
          keyword +
          " " +
          category +
          " " +
          total +
          " logged."
      );
    },
    resetState() {
      this.idsPulled = [];
      this.lastTimestamp = null;
      this.idsPulledDetails = [];
      this.lastTimestampDetails = null;
    },
  },
  watch: {
    refresh() {
      this.results = null;
      this.resetState();
      this.getContent();
      this.getFilterCounts();
      this.getNotifications();
    },
    refreshDataMap: {
      handler() {
        this.addDataToMap();
      },
      deep: true,
    },
    searchLibraryName: {
      handler(newValue) {
        if (newValue) {
          this.searchLibraryItemName = this.searchLibraryName
            ? this.searchLibraryName + " "
            : "";
        }
        if (!this.savedSearchDetails) {
          this.searchLibraryItemName = null;
        }
      },
    },
    mainMapMoveEnd: lodashDebounce(async function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clickBounds = null;
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      }
    }, 1000),
    keywords() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    keywordsBuilder: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
      deep: true,
    },
    dateRange() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    bounds() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    boundsType() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    aoiId: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
      deep: true,
    },
    places: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
      deep: true,
    },
    assets: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
      deep: true,
    },
    radius() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    poisDistUnits() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    events() {
      this.resetState();
      this.getContent();
      this.getFilterCounts({
        topicTotals: lodashGet(this.filterCounts, "topicTotals"),
      });
    },
    eventsV2() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    sources() {
      this.resetState();
      this.getContent();
      this.getFilterCounts({
        sourceTotals: lodashGet(this.filterCounts, "sourceTotals"),
      });
    },
    hotspotsOnly() {
      this.resetState();
      this.getContent();
      this.getFilterCounts();
    },
    "detailsSidebar.visible": {
      handler() {
        if (!this.detailsSidebar.visible) {
          this.clickBounds = null;
          this.currentType = null;
          this.features = null;
          this.featuresResults = null;
          this.resetState();
        }
      },
    },
    searchDetails: {
      handler() {
        if (this.searchType === "events") {
          this.showSidebar({
            bounds: this.aggregateBounds,
            searchDetails: this.keywords,
            noUpdateMap: true,
          });
          this.refresh = new Date().toISOString();
        }
      },
    },
    shouldPullData: {
      handler() {
        this.getContent();
        this.getFilterCounts();
        this.getNotifications();
      },
    },
    savedSearchDetails: {
      handler(newValue, oldValue) {
        if (!lodashIsEqual(newValue, oldValue)) {
          this.searchQueryName = this.savedSearchDetails
            ? this.savedSearchDetails.name + " "
            : "";
          this.searchLibraryItemName = null;
        }
      },
    },
    riskCategories: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
    },
    severity: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
      deep: true,
    },
    sentiment: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
    },
    emotions: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
    },
    geolocationPrecisions: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
    },
    riskCategoryTags: {
      handler() {
        this.resetState();
        this.getContent();
        this.getFilterCounts();
      },
    },
  },
};
</script>

<style scoped>
.hyp-header {
  font-size: 24px;
  vertical-align: text-bottom;
  display: flex;
  align-items: flex-start;
}
.hyp-header .results-header {
  flex-grow: 2;
}
.hyp-header .v-btn.charts-button {
  color: inherit;
  min-width: 0;
}
.hyp-header .v-btn.charts-button.disabled {
  opacity: 0.2;
  cursor: not-allowed;
}
.hyp-header .menu-icon {
  color: inherit;
}
.no-results-text {
  color: rgba(0, 0, 0, 0.87);
}

.small {
  font-size: 12px;
}

.search-query-name-display {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.new-badge {
  padding: 2px;
  background-color: #ff9463;
  color: white;
  border-radius: 4px;
  font-size: xx-small;
  margin-left: -5px;
  position: relative;
  top: -2px;
  float: right;
}
.v-overlay {
  left: 50% !important;
  background-color: rgba(0, 0, 0, 0) !important;
}

.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.307) !important;
  width: 50%;
}

.copy-overlay-indicator {
  width: 120px;
  height: 120px;
  background-color: green;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  font-size: large;
}
</style>
